import { Logger } from 'wdc-cube';
import { STATUS_TONES, STATUS_LABELS, STATUS_ORDER, formatNumber, measurementLabelOf } from '../../domain';
import { StatusBandsApi } from './status-bands.api';
import { finalOverlap, orderSaves, parseOverlap } from './save-order';
import { isCompanyTarget } from './status-bands.types';
import { BandScope, IndicatorScope, StatusBandsScope } from './status-bands.scope';
const LOG = Logger.get('Mormaii.StatusBandsPresenter');
// @Inject
const api = StatusBandsApi.INSTANCE;
/** Como se chama, na tela, o degrau de onde um valor herdado vem. */
const ORIGINS = {
    COMPANY: 'Empresa',
    DEFAULT: 'Plataforma'
};
/** A unidade de cada indicador, so para a tela dizer o que o numero significa. */
const UNITS = {
    102: 'mmHg (sistólica)',
    103: '°C',
    153: 'bpm',
    154: 'ms',
    155: '%',
    204: 'horas'
};
/**
 * Quem controla o editor de faixas de status.
 *
 * Duas telas o hospedam: "Meus dados" (faixas do responsavel, ou os padroes globais
 * se for super admin) e o detalhe do paciente (faixas daquele paciente). Sao os
 * tres degraus da MESMA cadeia de resolucao do backend, e por isso um editor so.
 *
 * E um PRESENTER sem place, e nao um "painel": painel e a visualizacao, e isto aqui
 * e quem a controla. Sem place pelo mesmo motivo do painel de analytics - um place
 * exigiria endereco proprio.
 */
export class StatusBandsPresenter {
    constructor(update, onSaved) {
        this.update = update;
        this.onSaved = onSaved;
        this.scope = new StatusBandsScope();
        this.target = 'tenants';
        /** As faixas como vieram, para o botao de descartar ter a que voltar. */
        this.loaded = [];
        this.scope.update = update;
        this.scope.onSave = this.save.bind(this);
        this.scope.onReset = this.reset.bind(this);
        this.scope.onRestoreDefaults = this.restoreDefaults.bind(this);
        this.scope.onFollowDefaults = this.followDefaults.bind(this);
    }
    /**
     * Carrega as faixas de um nivel.
     *
     * No nivel do paciente a busca desce a cadeia: se ele nao tem faixa propria,
     * mostram-se as do responsavel; se nem essas houver, os padroes globais. E o que
     * o backend faria ao classificar uma medicao dele, entao e o que o editor deve
     * mostrar - editar a partir de valores em branco faria o leitor inventar
     * limites em vez de ajustar os que valem.
     */
    async load(target) {
        this.target = target;
        this.scope.error = undefined;
        /*
         * O titulo diz DE QUEM sao estes limites, e a linha abaixo dele diz QUEM eles
         * alcancam.
         *
         * Os quatro niveis sao a mesma tela, e sem dono no titulo ela e ambigua: em
         * "Minha conta" o editor se chamava so "Editar Limites dos Parametros" e a
         * linha de origem dizia "Seguindo os limites padrao da plataforma" - lido
         * junto, parecia que ali se editavam os limites da plataforma. Quem edita
         * precisa saber se esta mexendo em si mesmo, na empresa ou em todo mundo,
         * antes de mexer.
         */
        if (typeof target === 'string') {
            const plataforma = target === 'defaults';
            this.scope.title = plataforma
                ? 'Editar Limites dos Parâmetros do Sistema'
                : 'Editar os Meus Limites dos Parâmetros';
            this.scope.appliesTo = plataforma
                ? 'Valem para toda a plataforma: cada empresa e cada pessoa que não tenha limites próprios.'
                : 'Valem só para as SUAS medições. Não alcançam os colaboradores da empresa.';
            this.scope.collapsible = false;
        }
        else if (isCompanyTarget(target)) {
            this.scope.title = 'Editar Limites dos Parâmetros da Empresa';
            this.scope.appliesTo = 'Valem para os colaboradores desta empresa que não tenham limites próprios.';
            this.scope.collapsible = false;
        }
        else {
            this.scope.title = 'Editar Limites dos Parâmetros do Colaborador';
            this.scope.appliesTo = 'Valem só para as medições desta pessoa.';
            this.scope.collapsible = true;
        }
        this.scope.canRestoreDefaults = target !== 'defaults';
        this.scope.canFollowDefaults = false;
        this.scope.inheritedFrom = undefined;
        try {
            /*
             * UMA ida ao servidor, e ela ja vem resolvida.
             *
             * O editor descia a cadeia aqui - buscava o nivel, e se viesse vazio
             * buscava o de cima, e o de cima do de cima. Agora o proprio servidor
             * devolve o que VALE em cada faixa, dizendo qual e propria e qual e
             * herdada (`own`), e de onde vem o valor herdado. A cadeia passou a ter
             * uma implementacao so, que e onde ela sempre deveria ter estado.
             */
            const bands = await api.fetchStatusBands(target);
            // Ha o que desfazer quando alguma faixa e PROPRIA deste nivel. Herdando
            // tudo, nao ha sobrescrita, e o botao prometeria uma acao sem efeito.
            this.scope.canFollowDefaults = target !== 'defaults' && bands.some((band) => band.own);
            /*
             * A linha de origem fala do CONJUNTO; as marcas falam de cada faixa.
             *
             * Herdando tudo, ela diz de onde vem. "Da empresa" basta UMA faixa vir
             * de la: a empresa e o degrau logo acima, e o que ela nao decide ela
             * repassa da plataforma - dizer "da plataforma" nesse caso esconderia
             * que ha decisao da empresa no meio.
             *
             * Com alguma faixa propria, a linha diz que ha decisao deste nivel, e
             * qual faixa diverge esta marcado ao lado de cada uma.
             */
            this.scope.inheritedFrom = bands.some((band) => band.own)
                ? undefined
                : bands.some((band) => band.inheritedFrom === 'COMPANY')
                    ? 'COMPANY'
                    : 'DEFAULT';
            this.loaded = bands;
            this.show(bands);
        }
        catch (caught) {
            LOG.warn('Could not load the status bands', caught);
            this.scope.error = 'Não foi possível carregar os parâmetros.';
        }
    }
    show(bands) {
        // Toda escrita programatica conta: e o que remonta os campos na tela.
        this.scope.revision += 1;
        const byType = new Map();
        for (const band of bands) {
            const list = byType.get(band.measurementTypeId) ?? [];
            list.push(band);
            byType.set(band.measurementTypeId, list);
        }
        this.scope.indicators = [...byType.entries()]
            .sort(([left], [right]) => left - right)
            .map(([typeId, list]) => {
            const indicator = new IndicatorScope();
            indicator.identity = typeId;
            indicator.measurementTypeId = typeId;
            /*
             * O nome vem do PAINEL, e nao do `measurementTypeName` da resposta.
             *
             * A referencia ecoa o nome do backend (`statuses[0].measurementTypeName`)
             * e era por ali que "Pressão" e "Temperatura" vazavam para uma tela
             * que chama as mesmas metricas de "Pressão Arterial" e "Temperatura
             * Corporal". O id e a chave estavel da resposta e o rotulo e texto de
             * exibicao do catalogo, entao o nome se resolve aqui, pelo id (B-03).
             */
            indicator.label = measurementLabelOf(typeId);
            indicator.unit = UNITS[typeId] ?? '';
            indicator.update = this.update;
            /*
             * Na ORDEM em que o backend as mandou, e nao em `STATUS_ORDER`.
             *
             * A referencia mapeia a lista como ela chega
             * (`statuses.map(...)`), e o backend a devolve do critico para o
             * otimo. Ordenar por `STATUS_ORDER` invertia as quatro faixas na
             * tela - e o diff de DOM nao acusava, porque casa os campos pelo
             * `name` e nao pela posicao. Foi a foto que mostrou.
             *
             * Um nivel que o backend nao devolveu nao aparece, tambem como la.
             */
            indicator.bands = list.map((band) => {
                const level = band.level;
                const scope = new BandScope();
                scope.identity = `${typeId}:${level}`;
                scope.level = level;
                scope.field = `${typeId}.${level}`;
                scope.label = STATUS_LABELS[level];
                scope.color = STATUS_TONES[level];
                scope.min = String(decode(typeId, band.minValue));
                scope.max = String(decode(typeId, band.maxValue));
                /*
                 * O que esta faixa tem de diferente do nivel de cima.
                 *
                 * Sem isto, quem edita nao sabe se aquele 60-80 e uma decisao
                 * deste nivel ou o valor do de cima passando atraves - e nao
                 * sabe o que desfaz ao voltar a herdar.
                 */
                scope.diverges = band.own && band.inheritedMinValue != null;
                scope.inherited = scope.diverges
                    ? `${ORIGINS[band.inheritedFrom ?? 'DEFAULT']}: ${decode(typeId, band.inheritedMinValue)} a ${decode(typeId, band.inheritedMaxValue)}`
                    : undefined;
                // O alcance do nivel de cima, para a conferencia de cobertura.
                scope.inheritedMin =
                    band.inheritedMinValue == null ? undefined : decode(typeId, band.inheritedMinValue);
                scope.inheritedMax =
                    band.inheritedMaxValue == null ? undefined : decode(typeId, band.inheritedMaxValue);
                scope.inheritedSource = band.inheritedFrom ?? undefined;
                scope.onMinChanged = (value) => {
                    scope.min = value;
                    this.scope.dirty = true;
                    // A cada tecla: o aviso tem de aparecer enquanto se edita.
                    this.checkCoverage();
                };
                scope.onMaxChanged = (value) => {
                    scope.max = value;
                    this.scope.dirty = true;
                    this.checkCoverage();
                };
                scope.update = this.update;
                return scope;
            });
            return indicator;
        });
        this.scope.dirty = false;
        // O que veio do servidor tambem passa pela conferencia: um vao pode ter
        // nascido de uma mudanca la em cima, e nao de quem esta editando agora.
        this.checkCoverage();
    }
    /**
     * Salva as faixas alteradas.
     *
     * O backend aceita UMA faixa por requisicao, entao sao varias - mas so as que
     * mudaram. Mandar as 24 sempre gastaria 24 chamadas para trocar um numero.
     *
     * Quando os valores foram HERDADOS, tudo conta como mudanca: salvar no nivel do
     * paciente significa criar as faixas dele, que ate entao nao existiam.
     */
    async save() {
        const changed = [];
        for (const indicator of this.scope.indicators) {
            for (const band of indicator.bands) {
                const min = Number(band.min);
                const max = Number(band.max);
                if (Number.isNaN(min) || Number.isNaN(max)) {
                    this.scope.error = `Valor inválido em ${indicator.label} / ${band.label}.`;
                    return;
                }
                if (min >= max) {
                    this.scope.error = `Em ${indicator.label} / ${band.label}, o mínimo precisa ser menor que o máximo.`;
                    return;
                }
                const original = this.loaded.find((candidate) => candidate.measurementTypeId === indicator.measurementTypeId && candidate.level === band.level);
                const encodedMin = encode(indicator.measurementTypeId, min);
                const encodedMax = encode(indicator.measurementTypeId, max);
                /*
                 * So o que MUDOU viaja - inclusive quando o nivel inteiro era
                 * herdado.
                 *
                 * Antes tudo era mandado nesse caso, porque salvar significava criar
                 * as faixas do nivel do zero. Agora quem materializa o indicador
                 * inteiro e o servidor, na primeira faixa que chega: as outras tres
                 * nascem la com o valor que estava valendo, e manda-las daqui seria
                 * repetir o que ele ja faz.
                 */
                if (!original || original.minValue !== encodedMin || original.maxValue !== encodedMax) {
                    changed.push({
                        measurementTypeId: indicator.measurementTypeId,
                        minValue: encodedMin,
                        maxValue: encodedMax,
                        level: band.level
                    });
                }
            }
        }
        if (changed.length === 0) {
            this.scope.error = undefined;
            this.scope.dirty = false;
            return;
        }
        /*
         * Por indicador: a sobreposicao que sobraria no FIM e recusada aqui, antes de
         * qualquer gravacao - o servidor a recusaria no meio do caminho, com parte das
         * faixas ja gravada. O resto vira uma ORDEM de gravacao em que nenhum passo
         * intermediario se sobrepoe (ver `orderSaves`).
         */
        const steps = [];
        for (const typeId of new Set(changed.map((band) => band.measurementTypeId))) {
            const current = this.loaded.filter((band) => band.measurementTypeId === typeId);
            const changes = changed.filter((band) => band.measurementTypeId === typeId);
            const overlap = finalOverlap(current, changes);
            if (overlap) {
                this.scope.error = overlapText(typeId, overlap);
                return;
            }
            for (const step of orderSaves(current, changes)) {
                steps.push({ ...step, measurementTypeId: typeId });
            }
        }
        this.scope.busy = true;
        this.scope.error = undefined;
        try {
            for (const band of steps) {
                try {
                    await api.saveStatusBand(this.target, {
                        measurementTypeId: band.measurementTypeId,
                        minValue: band.minValue,
                        maxValue: band.maxValue,
                        level: band.level
                    });
                }
                catch (caught) {
                    // A recusa do servidor chega com nivel em ingles e valor codificado.
                    const overlap = caught instanceof Error ? parseOverlap(caught.message) : undefined;
                    throw overlap ? new Error(overlapText(band.measurementTypeId, overlap)) : caught;
                }
            }
            await this.load(this.target);
            // Os avisos vao JUNTO com o "salvo": e o momento em que a pessoa acha
            // que terminou, e e a ultima chance de ela ler que ficou um vao.
            this.onSaved?.(changed.length, this.scope.warnings);
        }
        catch (caught) {
            this.scope.error = caught instanceof Error ? caught.message : 'Não foi possível salvar os parâmetros.';
        }
        finally {
            this.scope.busy = false;
        }
    }
    async reset() {
        this.show(this.loaded);
        this.scope.error = undefined;
        this.scope.dirty = false;
        /*
         * `update` explicito.
         *
         * Este painel nao e um presenter: nao ha acao do framework em volta do
         * clique, entao ninguem agenda o redesenho por ele. Sem esta linha o
         * "Cancelar" trocava os valores no scope e a tela seguia mostrando o que
         * havia sido digitado.
         */
        this.update(this.scope);
    }
    /**
     * Traz os PADROES da plataforma para a tela.
     *
     * Nao apaga nada no servidor: preenche os campos com os padroes e deixa o
     * formulario sujo, e quem grava e o "Confirmar Edições". E o que o
     * `onRestoreDefaults` da referencia faz - ela busca os defaults e os escreve nos
     * campos.
     */
    /**
     * As quatro faixas de um indicador precisam se encostar, e ninguem conferia.
     *
     * ## O que da errado sem isto
     *
     * A resolucao acontece por VALOR: uma medicao procura faixa neste nivel; nao
     * achando, desce para o de cima. Entao subir o "otimo" de 60 para 65 sem mexer
     * no "regular" (que termina em 60) abre um vao de 60 a 65 - e uma medicao de 62
     * nao fica sem resposta: ela e classificada pelo NIVEL DE CIMA, que ainda diz
     * "otimo comeca em 60". Quem subiu a regua nao subiu coisa alguma, e nada na
     * tela contava isso.
     *
     * Sobreposicao e o espelho: com "regular" ate 60 e "otimo" a partir de 55, uma
     * medicao de 57 casa com as duas, e a mesma pessoa entra na contagem de dois
     * niveis do painel.
     *
     * ## Vao avisa; sobreposicao impede
     *
     * Um vao pode ser deliberado - "abaixo de 45 nao e comigo". Recusar o salvamento
     * decidiria pelo cliente; ajustar o vizinho sozinho seria pior, porque mudaria um
     * numero que ninguem digitou. Entao a tela marca o campo, explica no icone e
     * repete no aviso de salvamento.
     *
     * A sobreposicao nao tem leitura deliberada, e o `save` a recusa (`finalOverlap`,
     * a mesma regra do servidor). Por isso as duas listas sao separadas: o aviso de
     * vao diz que da para salvar, e o de sobreposicao diz que nao da - um aviso so,
     * dizendo "da para salvar" sobre uma sobreposicao, prometia o que o "Confirmar"
     * recusava logo em seguida.
     *
     * Roda a cada tecla: o aviso tem de aparecer enquanto se edita, e nao so depois
     * de gravar.
     */
    checkCoverage() {
        const avisos = [];
        const vaos = [];
        const sobreposicoes = [];
        for (const indicator of this.scope.indicators) {
            indicator.warning = undefined;
            for (const band of indicator.bands) {
                band.warning = undefined;
            }
            /* Onde este indicador comeca a escrever em `avisos` - o que vier daqui e dele. */
            const meus = avisos.length;
            const faixas = indicator.bands.map((band) => ({
                band,
                min: Number(band.min),
                max: Number(band.max)
            }));
            // Meio da digitacao, ou faixa invertida: o `validate` do salvamento ja
            // reclama dessas, e conferir continuidade sobre numero quebrado so
            // produziria aviso falso.
            if (faixas.some((faixa) => Number.isNaN(faixa.min) || Number.isNaN(faixa.max) || faixa.min >= faixa.max)) {
                continue;
            }
            const unidade = indicator.unit ? ` ${indicator.unit}` : '';
            const ordenadas = [...faixas].sort((esquerda, direita) => esquerda.min - direita.min);
            for (let i = 0; i < ordenadas.length - 1; i += 1) {
                const atual = ordenadas[i];
                const proxima = ordenadas[i + 1];
                if (proxima.min < atual.max) {
                    // Os niveis na ordem e com o nome da tela, como na recusa do salvamento.
                    const par = pairOf(atual.band.level, proxima.band.level);
                    const ate = Math.min(atual.max, proxima.max);
                    const trecho = `entre ${proxima.min} e ${ate}${unidade}`;
                    const texto = `As faixas ${par} se sobrepõem ${trecho}: uma medição aí seria contada nos dois níveis. ` +
                        'Corrija antes de salvar.';
                    marcar(atual.band, texto);
                    marcar(proxima.band, texto);
                    const aviso = `${indicator.label}: as faixas ${par} se sobrepõem ${trecho}.`;
                    avisos.push(aviso);
                    sobreposicoes.push(aviso);
                    continue;
                }
                if (proxima.min > atual.max) {
                    const texto = this.gapText(atual.max, proxima.min, unidade, indicator);
                    marcar(atual.band, texto);
                    marcar(proxima.band, texto);
                    const aviso = `${indicator.label}: nada cobre de ${atual.max} a ${proxima.min}${unidade}.`;
                    avisos.push(aviso);
                    vaos.push(aviso);
                }
            }
            /*
             * As PONTAS, medidas contra o que o nivel de cima cobre.
             *
             * Estreitar a escala inteira - dizer que abaixo de 45 nao interessa -
             * tem o mesmo efeito de um vao no meio: a medicao de 42 nao some, ela
             * passa a ser classificada la em cima.
             */
            const herdadoMin = Math.min(...faixas.map((faixa) => faixa.band.inheritedMin ?? faixa.min));
            const herdadoMax = Math.max(...faixas.map((faixa) => faixa.band.inheritedMax ?? faixa.max));
            const primeira = ordenadas[0];
            const ultima = ordenadas[ordenadas.length - 1];
            if (primeira.min > herdadoMin) {
                const texto = this.gapText(herdadoMin, primeira.min, unidade, indicator);
                marcar(primeira.band, texto);
                const aviso = `${indicator.label}: nada cobre de ${herdadoMin} a ${primeira.min}${unidade}.`;
                avisos.push(aviso);
                vaos.push(aviso);
            }
            if (ultima.max < herdadoMax) {
                const texto = this.gapText(ultima.max, herdadoMax, unidade, indicator);
                marcar(ultima.band, texto);
                const aviso = `${indicator.label}: nada cobre de ${ultima.max} a ${herdadoMax}${unidade}.`;
                avisos.push(aviso);
                vaos.push(aviso);
            }
            /*
             * O aviso do INDICADOR e a juncao dos problemas dele, sem o prefixo do
             * nome - quem le o icone ja esta olhando para o nome.
             */
            if (avisos.length > meus) {
                indicator.warning = avisos
                    .slice(meus)
                    .map((aviso) => aviso.slice(indicator.label.length + 2))
                    .join(' ');
            }
        }
        this.scope.warnings = avisos;
        this.scope.gaps = vaos;
        this.scope.overlaps = sobreposicoes;
    }
    /**
     * O que acontece com uma medicao que cai no vao - e sao duas coisas diferentes.
     *
     * Se o nivel de CIMA cobre aquele intervalo, ele responde, e o limite que se
     * acabou de escrever nao vale ali. Se ele tambem nao cobre, a medicao fica sem
     * classificacao nenhuma - e ai o vao e uma decisao, e nao um descuido.
     */
    gapText(de, ate, unidade, indicator) {
        const coberta = indicator.bands.find((band) => band.inheritedMin !== undefined &&
            band.inheritedMax !== undefined &&
            band.inheritedMin <= de &&
            band.inheritedMax >= ate);
        if (!coberta) {
            return `Nada cobre de ${de} a ${ate}${unidade}: uma medição aí fica sem classificação.`;
        }
        const acima = coberta.inheritedSource === 'COMPANY' ? 'pela empresa' : 'pela plataforma';
        return (`Nada cobre de ${de} a ${ate}${unidade}: uma medição aí é classificada ${acima}, ` +
            'e não por este limite.');
    }
    /**
     * Apaga a sobrescrita e volta a HERDAR.
     *
     * Diferente de `restoreDefaults`, que copia os padroes para os campos e espera o
     * "Confirmar Edicoes": ali o paciente fica com faixa propria, igual a do padrao
     * naquele instante, e para de acompanhar quando o padrao mudar. Aqui ele volta a
     * seguir a cadeia - paciente, responsavel, sistema.
     *
     * Recarrega depois de apagar, e nao adivinha o resultado: quem decide o que o
     * paciente passa a herdar e o servidor, e pode ser o nivel do responsavel e nao
     * o do sistema.
     */
    async followDefaults() {
        if (this.target === 'defaults') {
            return;
        }
        this.scope.busy = true;
        this.scope.error = undefined;
        try {
            await api.resetStatusBands(this.target);
            await this.load(this.target);
        }
        catch (caught) {
            LOG.warn('Could not reset the patient bands', caught);
            this.scope.error = caught instanceof Error ? caught.message : 'Não foi possível voltar aos padrões.';
        }
        finally {
            this.scope.busy = false;
            this.update(this.scope);
        }
    }
    async restoreDefaults() {
        this.scope.busy = true;
        this.scope.error = undefined;
        try {
            this.show(await api.fetchStatusBands('defaults'));
            this.scope.dirty = true;
        }
        catch (caught) {
            LOG.warn('Could not load the platform defaults', caught);
            this.scope.error = caught instanceof Error ? caught.message : 'Não foi possível buscar os padrões.';
        }
        finally {
            this.scope.busy = false;
            this.update(this.scope);
        }
    }
}
/**
 * Duas medidas sao guardadas numa escala que ninguem digita.
 *
 * A PRESSAO e `sistolica*1000+diastolica`: sem converter, dizer 120 exigiria
 * digitar `120000`. O SONO e guardado em SEGUNDOS: sem converter, dizer sete
 * horas exigiria digitar `25200`.
 *
 * Os dois fatores sao os mesmos do `edit-parameters.tsx` do painel antigo - 1000
 * e 3600 - e existem aqui para a tela poder ser lida.
 */
const SCALE = {
    102: 1000,
    204: 3600
};
function decode(typeId, value) {
    const scale = SCALE[typeId];
    return scale ? Math.round((value / scale) * 100) / 100 : value;
}
function encode(typeId, value) {
    const scale = SCALE[typeId];
    return scale ? Math.round(value * scale) : value;
}
/**
 * A recusa de sobreposicao em palavras de quem le: nivel como a tela o chama, valor
 * na unidade em que se digita.
 *
 * "As faixas EXCELLENT e GOOD se sobrepõem entre 120000.00 e 125000" era o que o
 * servidor dizia, e chegava assim ao usuario.
 */
function overlapText(typeId, overlap) {
    const unit = UNITS[typeId]?.replace(/\s*\(.*\)$/, '');
    const value = (encoded) => formatNumber(decode(typeId, encoded), 0, 2);
    return (`As faixas ${pairOf(overlap.level, overlap.other)} ` +
        `de ${measurementLabelOf(typeId)} se sobrepõem entre ${value(overlap.from)} e ${value(overlap.to)}` +
        `${unit ? ` ${unit}` : ''}: uma medição aí seria contada nos dois níveis.`);
}
/**
 * Dois niveis como a tela os chama, do melhor para o pior: "Ótimo e Bom".
 *
 * A ordem e fixa para o aviso da tela e a recusa do salvamento falarem igual - antes um
 * dizia "bom e ótimo" e o outro "Ótimo e Bom", sobre a mesma sobreposicao.
 */
function pairOf(one, other) {
    const rank = (level) => {
        const index = STATUS_ORDER.indexOf(level);
        return index < 0 ? STATUS_ORDER.length : index;
    };
    const label = (level) => STATUS_LABELS[level] ?? level;
    const [first, second] = rank(one) <= rank(other) ? [one, other] : [other, one];
    return `${label(first)} e ${label(second)}`;
}
/** Acumula os avisos de uma faixa: dois problemas na mesma nao se escondem. */
function marcar(band, texto) {
    band.warning = band.warning ? `${band.warning} ${texto}` : texto;
}
//# sourceMappingURL=status-bands.presenter.js.map